import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, AlertCircle, Clock } from 'lucide-react';
import type { DurationPercentilesData } from '../api/dashboard.schemas';

interface DurationChartProps {
  data?: DurationPercentilesData;
  loading?: boolean;
  error?: Error;
}

/**
 * DurationChart Component
 * 
 * Displays validation duration percentiles to show system performance.
 * Helps understand how long verifications take and identify performance issues.
 */
export function DurationChart({ 
  data, 
  loading = false, 
  error
}: DurationChartProps) {
  // Loading state
  if (loading) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Validation Duration</CardTitle>
          </div>
          <CardDescription>Performance percentiles in seconds</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
            Loading chart...
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Chart Error</CardTitle>
          </div>
          <CardDescription>Failed to load duration data</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-destructive/10 to-destructive/5 rounded-xl flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive/60" />
            </div>
            <div className="space-y-2">
              <h3 className="font-medium">Unable to display chart</h3>
              <p className="text-sm text-muted-foreground">{error.message}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No data state
  if (!data || data.data.length === 0) {
    return (
      <Card className="h-full flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle className="text-base">Validation Duration</CardTitle>
          </div>
          <CardDescription>Performance percentiles in seconds</CardDescription>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="text-center text-muted-foreground">
            <Clock className="h-6 w-6 mx-auto mb-2" />
            <p>No duration data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Calculate max value for scaling (keep percentiles in logical order)
  const maxValue = Math.max(...data.data.map(item => item.value));

  // Format duration for display
  const formatDuration = (seconds: number): string => {
    if (seconds < 60) {
      return `${seconds.toFixed(1)}s`;
    } else if (seconds < 3600) {
      return `${(seconds / 60).toFixed(1)}m`;
    } else {
      return `${(seconds / 3600).toFixed(1)}h`;
    }
  };

  // Get color based on percentile (faster = green, slower = red)
  const getColor = (name: string): string => {
    switch (name.toLowerCase()) {
      case 'min': return 'bg-green-500';
      case 'p25': return 'bg-green-400';
      case 'p50': return 'bg-yellow-500';
      case 'p75': return 'bg-orange-500';
      case 'p90': return 'bg-red-500';
      case 'max': return 'bg-red-600';
      case 'avg': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get description for each percentile
  const getDescription = (name: string): string => {
    switch (name.toLowerCase()) {
      case 'min': return 'Fastest verification';
      case 'p25': return '25% complete within';
      case 'p50': return '50% complete within (median)';
      case 'p75': return '75% complete within';
      case 'p90': return '90% complete within';
      case 'max': return 'Slowest verification';
      case 'avg': return 'Average duration';
      default: return '';
    }
  };

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="flex-shrink-0">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-primary" />
          <CardTitle className="text-base">Validation Duration</CardTitle>
        </div>
        <CardDescription>
          Performance percentiles in {data.unit}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 space-y-4 overflow-y-auto">
          {data.data.map((item, index) => {
            const barWidth = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
            const colorClass = getColor(item.name);
            const description = getDescription(item.name);
            
            return (
              <div key={index} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="min-w-0 flex-1">
                    <span className="font-medium">{item.name}</span>
                    {description && (
                      <span className="text-muted-foreground ml-2 text-xs">- {description}</span>
                    )}
                  </div>
                  <span className="font-semibold flex-shrink-0 ml-2">
                    {formatDuration(item.value)}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-3">
                  <div
                    className={`${colorClass} h-3 rounded-full transition-all duration-500 ease-out`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Performance summary */}
        <div className="mt-4 p-3 bg-muted/50 rounded-lg flex-shrink-0">
          <h4 className="font-medium text-sm mb-2">Performance Summary</h4>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-muted-foreground">Median:</span>
              <span className="ml-2 font-medium">
                {formatDuration(data.data.find(d => d.name.toLowerCase() === 'p50')?.value || 0)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Average:</span>
              <span className="ml-2 font-medium">
                {formatDuration(data.data.find(d => d.name.toLowerCase() === 'avg')?.value || 0)}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}